import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Formik } from 'formik';
import Moment from 'moment';
import {
  AppDatePicker,
  AppFileUploader,
  AppModal,
  AppNotificationAlert,
  AppRemixIcon,
  AppTextArea,
} from '@/layout/AppComponents';
import Translator from '../../../../components/helpers/Translator';
import { axiosPath } from '../../../../../routing/helpers/router';
import colors from '../../../../../constants/colors';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import minions from '../../../../../minions/minions';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import _ from 'lodash';

const GCLStages = () => {
  const { moduleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const global = useSelector((state) => state.global);
  const [selectedStageGate, setSelectedStageGate] = useState(0);
  const [selectedOptionsGroup, setSelectedOptionsGroup] = useState();
  const options = useSelector((state) => state.currentPage?.options);
  const stageGates = useSelector(
    (state) => state.currentPage?.options?.stageGates,
  );
  const poLevel1 = useSelector(
    (state) => state.currentPage?.plantObligationLevels?.level1,
  );
  const poLevel3 = useSelector(
    (state) => state.currentPage?.plantObligationLevels?.level3,
  );

  // > LOAD STAGE GATES
  const loadStageGates = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_legalCompliance_stageGate_list'), {
          moduleBridgeHex,
          plantHex,
        })
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change('options', {
                stageGates: r.data,
              }),
            );
            dispatch(
              currentPageAction.change(
                'visiblePlantObligations',
                r?.data?.groups?.[selectedStageGate]?.poIDs,
                true,
              ),
            );
          }
        }),
    [],
  );

  useEffect(() => {
    loadStageGates();
  }, [moduleBridgeHex]);
  // < LOAD STAGE GATES

  // > EDIT STAGE GATE
  const handleEditStageGate = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath('api_r_user_module_legalCompliance_stageGate_options_edit'),
          minions.forms.toFormData({
            moduleBridgeHex,
            plantHex,
            ...payload,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                'options',
                { ...options, stageGates: { ...stageGates, options: r.data } },
                true,
              ),
            );
            setSelectedOptionsGroup(undefined);
          }
        })
        .catch((err) => {
          dispatch(errorHandler(err));
        }),
    [moduleBridgeHex, plantHex, stageGates],
  );
  // < EDIT STAGE GATE

  const handleGateColor = useCallback(
    (groupIndex) =>
      stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.state
        ? stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.state === 1
          ? colors.success
          : colors.goWithConditions
        : colors.grey600,
    [plantHex, stageGates],
  );

  const handleLineColor = useCallback(
    (sg) => {
      const plantObligations = sg?.poIDs?.map((id) => ({
        state: poLevel1?.[id]?.state,
        isCritical: !!stageGates?.criticalPlantObligationIds?.includes(id),
      }));

      const states = _.map(plantObligations, 'state');
      const uniqueState = _.uniq(states).filter((s) => s !== 4);

      if (
        uniqueState.length === 0 ||
        (uniqueState.length === 1 && uniqueState[0] === 1)
      ) {
        return colors.success;
      }
      if (uniqueState.length === 1 && uniqueState[0] === 0) {
        return colors.grey600;
      }

      let isYellow = false;
      let isRed = false;
      plantObligations?.map((po) => {
        if (
          (po.state === 0 && po.isCritical) ||
          (po.state === 3 && po.isCritical)
        ) {
          isRed = true;
        }
        if (
          (po.state === 0 && !po.isCritical) ||
          (po.state === 3 && !po.isCritical)
        ) {
          isYellow = true;
        }
      });

      if (isRed) return colors.danger;
      if (isYellow) return colors.goWithConditions;

      return colors.grey600;
    },
    [poLevel1, stageGates],
  );

  const handleUpload = useCallback((values, setFieldValue, opt, urls) => {
    const files = Array.from(opt).filter(
      (f) =>
        !values.files.includes(f.name) && (!urls || !urls.includes(f.name)),
    );
    setFieldValue('files', [...values.files, ...files]);
  }, []);

  const toggleRemoveFile = useCallback((values, setFieldValue, fileName) => {
    const updateRemoveFiles = values.removeFiles;

    if (updateRemoveFiles.includes(fileName)) {
      setFieldValue(
        'removeFiles',
        updateRemoveFiles.filter((fn) => fn !== fileName),
      );
    } else {
      updateRemoveFiles.push(fileName);
      setFieldValue('removeFiles', updateRemoveFiles);
    }
  }, []);

  const calculatePlantObligations = useCallback(
    (stageGate, state, isCritical) => {
      const plantObligations = stageGate?.poIDs?.filter(
        (poID) =>
          poLevel1?.[poID]?.state === state &&
          (isCritical
            ? stageGates?.criticalPlantObligationIds?.includes(poID)
            : !stageGates?.criticalPlantObligationIds?.includes(poID)),
      );

      return plantObligations;
    },
    [poLevel1],
  );

  const addDelimitation = useCallback(
    (plantObligations, groupIndex) => {
      setSelectedStageGate(groupIndex);
      dispatch(
        currentPageAction.change(
          'visiblePlantObligations',
          plantObligations,
          true,
        ),
      );
      dispatch(
        currentPageAction.change(
          'options',
          {
            ...options,
            stageGates: { ...stageGates, delimitation: groupIndex },
          },
          true,
        ),
      );
      setSelectedOptionsGroup(undefined);
    },
    [poLevel1, currentPageAction, selectedStageGate, stageGates],
  );

  const renderDelimitation = useCallback(
    (name, stageGate, groupIndex, state, isCritical) => {
      const plantObligations = calculatePlantObligations(
        stageGate,
        state,
        isCritical,
      );
      const poLength = plantObligations?.length || 0;

      return (
        <li
          className={`mb-1 ${poLength > 0 ? 'pointer' : ''} ${
            poLength > 0 ? '' : 'font-italic'
          }`}
          title={poLength > 0 ? 'Delimitate' : ''}
          onClick={() =>
            poLength > 0
              ? addDelimitation(plantObligations, groupIndex)
              : undefined
          }
        >
          <AppRemixIcon
            icon="filter"
            className="mr-2"
            color={state === 0 ? colors.unfulfilled : colors.danger}
            filled
          />
          {name} ({poLength})
        </li>
      );
    },
    [
      poLevel1,
      currentPageAction,
      selectedStageGate,
      addDelimitation,
      calculatePlantObligations,
    ],
  );

  const handleFreshGroupSelection = useCallback(
    (sg) => {
      dispatch(
        currentPageAction.change('visiblePlantObligations', sg?.poIDs, true),
      );
      dispatch(
        currentPageAction.change(
          'options',
          {
            ...options,
            stageGates: { ...stageGates, delimitation: undefined },
          },
          true,
        ),
      );
    },
    [poLevel1, currentPageAction, selectedStageGate, stageGates],
  );

  useEffect(() => {
    if (stageGates?.groups?.[selectedStageGate]) {
      handleFreshGroupSelection(stageGates?.groups?.[selectedStageGate]);
    }
  }, [selectedStageGate]);

  const handleCloseGate = useCallback(
    (lineColor, groupIndex) => {
      if (
        lineColor === colors.goWithConditions &&
        (!stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.comment ||
          !stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.date)
      ) {
        AppNotificationAlert({
          title: 'Warning',
          message: 'Comment and date required',
          type: 'warning',
          duration: 5000,
        });
        return;
      }

      if (lineColor === colors.grey600 || lineColor === colors.danger) return;

      axios
        .post(axiosPath('api_r_user_module_legalCompliance_stageGate_close'), {
          moduleBridgeHex,
          plantHex,
          groupIndex,
          state: lineColor === colors.success ? 1 : 2,
        })
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                'options',
                {
                  ...options,
                  stageGates: { ...stageGates, options: r.data },
                },
                true,
              ),
            );
            AppNotificationAlert({
              title: 'Completed',
              message: 'Gate has been closed',
              type: 'success',
              duration: 5000,
            });
            setSelectedOptionsGroup(undefined);
          }
        });
    },
    [
      moduleBridgeHex,
      plantHex,
      currentPageAction,
      stageGates,
      selectedStageGate,
    ],
  );

  const handleReopenGate = useCallback(
    (groupIndex) =>
      axios
        .post(axiosPath('api_r_user_module_legalCompliance_stageGate_reopen'), {
          moduleBridgeHex,
          plantHex,
          groupIndex,
        })
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                'options',
                {
                  ...options,
                  stageGates: { ...stageGates, options: r.data },
                },
                true,
              ),
            );
            AppNotificationAlert({
              title: 'Completed',
              message: 'Gate has been opened',
              type: 'success',
              duration: 5000,
            });
            setSelectedOptionsGroup(undefined);
          }
        }),
    [poLevel1, currentPageAction, stageGates],
  );

  const isPreviousGatesOpened = useCallback(
    (groupIndex) => {
      let isPreviousOpened = false;

      stageGates?.groups.map((g, index) => {
        if (
          !stageGates?.options?.[plantHex]?.[`gate${index}`]?.state &&
          index < groupIndex
        ) {
          isPreviousOpened = true;
        }
      });

      return isPreviousOpened;
    },
    [stageGates],
  );

  const isCloseProjectBtn = useCallback(() => {
    let isBtn = true;

    stageGates?.groups.map((g, index) => {
      if (!stageGates?.options?.[plantHex]?.[`gate${index}`]?.state) {
        isBtn = false;
      }
    });

    return isBtn;
  }, [stageGates]);

  const handleToggleProject = useCallback(
    () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_legalCompliance_stageGate_toggleProject',
          ),
          {
            moduleBridgeHex,
            plantHex,
          },
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                'options',
                {
                  ...options,
                  stageGates: r.data,
                },
                true,
              ),
            );
            AppNotificationAlert({
              title: 'Completed',
              message: r.data?.options?.[plantHex]?.isProjectClosed
                ? 'Project has been closed'
                : 'Project has been opened',
              type: 'success',
              duration: 5000,
            });
            setSelectedOptionsGroup(undefined);
          }
        }),
    [poLevel1, currentPageAction, stageGates],
  );

  const handleCloseBtnOutside = useCallback(
    (sg, groupIndex) => {
      if (stageGates?.options?.[plantHex]?.isProjectClosed) {
        return <ChangeStateBtn disabled>Status</ChangeStateBtn>;
      }
      if (handleLineColor(sg) === colors.goWithConditions) {
        return (
          <ChangeStateBtn
            title="Go with conditions"
            color={colors.goWithConditions}
            disabled={
              isPreviousGatesOpened(groupIndex) ||
              !stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]
                ?.comment ||
              stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]
                ?.comment === '' ||
              !stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.date ||
              stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.date ===
                ''
            }
            onClick={() => handleCloseGate(handleLineColor(sg), groupIndex)}
          >
            Status
          </ChangeStateBtn>
        );
      }
      if (
        handleLineColor(sg) === colors.danger ||
        handleLineColor(sg) === colors.grey600
      ) {
        return (
          <ChangeStateBtn title="No go" color={colors.danger} disabled>
            Status
          </ChangeStateBtn>
        );
      }
      return (
        <ChangeStateBtn
          disabled={isPreviousGatesOpened(groupIndex)}
          title="Go"
          color={colors.success}
          onClick={() => handleCloseGate(handleLineColor(sg), groupIndex)}
        >
          Status
        </ChangeStateBtn>
      );
    },
    [stageGates, plantHex, handleCloseGate, handleLineColor],
  );

  const handleCloseBtn = useCallback(
    (sg, groupIndex, isFormDirty) => {
      if (stageGates?.options?.[plantHex]?.isProjectClosed) {
        return (
          <button
            type="button"
            className="btn btn-outline-light btn-sm"
            disabled
          >
            Reopen
          </button>
        );
      }
      if (handleLineColor(sg) === colors.goWithConditions) {
        return (
          <YellowBtn
            type="button"
            className="btn btn-sm"
            disabled={
              isFormDirty ||
              isPreviousGatesOpened(groupIndex) ||
              !stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]
                ?.comment ||
              stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]
                ?.comment === '' ||
              !stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.date ||
              stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.date ===
                ''
            }
            onClick={() => handleCloseGate(handleLineColor(sg), groupIndex)}
          >
            Go with conditions
          </YellowBtn>
        );
      }
      if (
        handleLineColor(sg) === colors.danger ||
        handleLineColor(sg) === colors.grey600
      ) {
        return (
          <button type="button" className="btn btn-danger btn-sm" disabled>
            No go
          </button>
        );
      }
      return (
        <button
          type="button"
          className="btn btn-success btn-sm"
          disabled={isFormDirty || isPreviousGatesOpened(groupIndex)}
          onClick={() => handleCloseGate(handleLineColor(sg), groupIndex)}
        >
          Go
        </button>
      );
    },
    [stageGates, plantHex, handleCloseGate, handleLineColor],
  );

  const handleGetFile = useCallback(
    async (groupIndex, fileIndex) => {
      axios
        .post(
          axiosPath('api_r_user_module_legalCompliance_stageGate_file_get'),
          {
            moduleBridgeHex,
            plantHex,
            groupIndex,
            fileIndex,
          },
        )
        .then(({ data }) => {
          window.open(data, '_blank', 'noopener,noreferrer');
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, plantHex],
  );

  if (!stageGates || !poLevel1) return <></>;

  return (
    <>
      <Steps id="progressbar" className="text-center">
        {stageGates?.groups?.map((sg, groupIndex) => (
          <Step
            key={groupIndex}
            gateColor={handleGateColor(groupIndex)}
            lineColor={handleLineColor(sg)}
            isSelected={selectedStageGate === groupIndex}
          >
            <div className="date d-flex justify-content-center align-center mb-2">
              {stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]
                ?.approvedDate
                ? stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]
                    ?.approvedDate
                : ''}
            </div>
            <>
              {stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]?.state ? (
                <ChangeStateBtn
                  disabled={stageGates?.options?.[plantHex]?.isProjectClosed}
                  onClick={() => handleReopenGate(groupIndex)}
                  color={colors.grey400}
                  title="Reopen"
                >
                  Status
                </ChangeStateBtn>
              ) : (
                <>{handleCloseBtnOutside(sg, groupIndex)}</>
              )}
            </>
            <div className="progress-line" />
            <div
              className="number"
              onClick={() => setSelectedStageGate(groupIndex)}
            >
              Gate {groupIndex}
            </div>
            <div
              className="d-flex pointer justify-content-center align-center"
              title={`Edit gate ${groupIndex}`}
              onClick={() => setSelectedOptionsGroup(groupIndex)}
            >
              {stageGates?.delimitation === groupIndex && (
                <span className="d-block mr-1">
                  <AppRemixIcon
                    icon="filter"
                    size={18}
                    color={colors.primary}
                    filled
                  />
                </span>
              )}
              <AppRemixIcon
                icon="information"
                size={20}
                filled
                color={colors.primaryLight}
              />
            </div>
            <AppModal
              show={selectedOptionsGroup === groupIndex}
              title={`Edit gate ${groupIndex}`}
              size="xl"
              onClose={() => setSelectedOptionsGroup(undefined)}
            >
              <Formik
                initialValues={{
                  groupIndex,
                  comment:
                    stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]
                      ?.comment || '',
                  date:
                    stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]
                      ?.date || '',
                  approvedDate:
                    stageGates?.options?.[plantHex]?.[`gate${groupIndex}`]
                      ?.approvedDate || Moment().format('DD/MM/YYYY'),
                  removeFiles: [],
                  files: [],
                }}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={handleEditStageGate}
              >
                {({
                  handleSubmit,
                  values,
                  setFieldValue,
                  errors,
                  isSubmitting,
                  dirty,
                }) => (
                  <form onSubmit={handleSubmit}>
                    {stageGates?.delimitation === groupIndex && (
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="alert alert-info pointer">
                            <span onClick={() => handleFreshGroupSelection(sg)}>
                              <AppRemixIcon
                                icon="delete-bin-6"
                                className="mr-2"
                              />
                              Delete delimitation
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="row">
                      <div className="col-lg-4">
                        {selectedStageGate === groupIndex && (
                          <FiltersList>
                            {renderDelimitation(
                              'Critics without fulfillment', // Criticas sin cumplimentar
                              sg,
                              groupIndex,
                              0,
                              true,
                            )}
                            {renderDelimitation(
                              'Non critics without fulfillment', // No criticas sin cumplimentar
                              sg,
                              groupIndex,
                              0,
                              false,
                            )}
                            {renderDelimitation(
                              'Unfulfilled critics', // Criticas incumplidas
                              sg,
                              groupIndex,
                              3,
                              true,
                            )}
                            {renderDelimitation(
                              'Unfulfilled non critics', // No criticas incumplidas
                              sg,
                              groupIndex,
                              3,
                              false,
                            )}
                          </FiltersList>
                        )}
                      </div>
                      <div className="col-lg-8">
                        <div className="row">
                          <div className="col-lg-12">
                            <AppTextArea
                              title="Comment"
                              icon={
                                <AppRemixIcon icon="align-left" noVariants />
                              }
                              onChange={(opt) => setFieldValue('comment', opt)}
                              value={values.comment}
                              error={errors.comment}
                              isDisabled={
                                stageGates?.options?.[plantHex]?.[
                                  `gate${groupIndex}`
                                ]?.state
                              }
                            />
                            {handleLineColor(sg) === colors.goWithConditions &&
                              (!values?.comment || values?.comment === '') && (
                                <span className="text-danger">
                                  *Comment is required to close this gate
                                </span>
                              )}
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-lg-6">
                            <AppDatePicker
                              title="Date"
                              value={values.date}
                              error={errors.date}
                              onChange={(opt) =>
                                setFieldValue('date', opt || '')
                              }
                              disabled={
                                stageGates?.options?.[plantHex]?.[
                                  `gate${groupIndex}`
                                ]?.state
                              }
                              popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                              isClearable
                            />
                            {handleLineColor(sg) === colors.goWithConditions &&
                              (!values?.date || values?.date === '') && (
                                <span className="text-danger">
                                  *Date is required to close this gate
                                </span>
                              )}
                          </div>
                          <div className="col-lg-6">
                            <div
                              className="row"
                              style={{
                                overflowY: 'scroll',
                                maxHeight: '113px',
                              }}
                            >
                              {stageGates?.options?.[plantHex]?.[
                                `gate${groupIndex}`
                              ]?.urls?.length > 0 && (
                                <div className="col-lg-12">
                                  <EvidenceFileList urls>
                                    {stageGates?.options?.[plantHex]?.[
                                      `gate${groupIndex}`
                                    ]?.urls.map((fileName, fileIndex) => (
                                      <li key={fileName}>
                                        <AppRemixIcon
                                          className="pointer mr-3"
                                          icon="delete-bin"
                                          color={
                                            values.removeFiles.includes(
                                              fileName,
                                            )
                                              ? colors.grey400
                                              : colors.danger
                                          }
                                          title="Click to delete"
                                          onClick={() =>
                                            toggleRemoveFile(
                                              values,
                                              setFieldValue,
                                              fileName,
                                            )
                                          }
                                        />
                                        {minions.files.fileIcon(
                                          fileName,
                                          true,
                                          values.removeFiles.includes(fileName)
                                            ? colors.grey400
                                            : null,
                                          'mr-2',
                                        )}
                                        <FileName
                                          onClick={() =>
                                            handleGetFile(groupIndex, fileIndex)
                                          }
                                          // href={`${global.urlData.userApiUrl}m/legal-compliance/evaluation/stage-gate/options/get-file/${moduleBridgeHex}/${plantHex}/${groupIndex}/${fileIndex}/`}
                                          title="Download"
                                          isRemoved={values.removeFiles.includes(
                                            fileName,
                                          )}
                                        >
                                          {fileName}
                                        </FileName>
                                      </li>
                                    ))}
                                  </EvidenceFileList>
                                </div>
                              )}
                              <div>
                                {values?.files?.length > 0 && <hr />}
                                <div className="col-lg-12">
                                  <EvidenceFileList urls={false}>
                                    {values.files.map((f) => (
                                      <li
                                        key={f.name}
                                        title="Click to delete"
                                        onClick={() =>
                                          setFieldValue(
                                            'files',
                                            values.files.filter(
                                              (file) => file.name !== f.name,
                                            ),
                                          )
                                        }
                                      >
                                        <AppRemixIcon
                                          className="pointer mr-3"
                                          icon="delete-bin"
                                          color={colors.danger}
                                        />
                                        {minions.files.fileIcon(
                                          f.name,
                                          true,
                                          null,
                                          'mr-2',
                                        )}
                                        {f.name}
                                      </li>
                                    ))}
                                  </EvidenceFileList>
                                </div>
                              </div>
                            </div>
                            <div>
                              <AppFileUploader
                                isMulti
                                placeholder={
                                  values.files.length === 0 ? (
                                    'Select one or multiple files'
                                  ) : (
                                    <Translator
                                      file="general"
                                      name="select.number.of.elements"
                                      variables={{
                                        elements: values.files.length,
                                      }}
                                    />
                                  )
                                }
                                onChange={(opt) =>
                                  handleUpload(
                                    values,
                                    setFieldValue,
                                    opt,
                                    stageGates?.options?.[plantHex]?.[
                                      `gate${groupIndex}`
                                    ]?.urls,
                                  )
                                }
                                btnText="Search..."
                                isDisabled={
                                  stageGates?.options?.[plantHex]?.[
                                    `gate${groupIndex}`
                                  ]?.state
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-12 mt-3">
                        {!stageGates?.options?.[plantHex]?.isProjectClosed && (
                          <>
                            {stageGates?.options?.[plantHex]?.[
                              `gate${groupIndex}`
                            ]?.state ? (
                              <button
                                type="button"
                                className="btn btn-outline-light btn-sm"
                                disabled={
                                  isSubmitting ||
                                  stageGates?.options?.[plantHex]
                                    ?.isProjectClosed
                                }
                                onClick={() => handleReopenGate(groupIndex)}
                                title="Reopen"
                              >
                                Reopen
                              </button>
                            ) : (
                              <>{handleCloseBtn(sg, groupIndex, dirty)}</>
                            )}
                          </>
                        )}
                        {groupIndex === stageGates?.groups.length - 1 &&
                          isCloseProjectBtn() && (
                            <button
                              type="button"
                              className={`btn btn-${
                                stageGates?.options?.[plantHex]?.isProjectClosed
                                  ? 'outline-'
                                  : ''
                              }info btn-sm ml-2`}
                              onClick={() => handleToggleProject()}
                            >
                              {stageGates?.options?.[plantHex]?.isProjectClosed
                                ? 'Reopen project'
                                : 'Close project'}
                            </button>
                          )}
                        <AprovedDateWrapper className="d-inline-block ml-2">
                          <AppDatePicker
                            title="Approval date"
                            value={values.approvedDate}
                            onChange={(opt) =>
                              setFieldValue('approvedDate', opt)
                            }
                            disabled={
                              stageGates?.options?.[plantHex]?.[
                                `gate${groupIndex}`
                              ]?.state
                            }
                            popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                          />
                        </AprovedDateWrapper>
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm float-right"
                          disabled={
                            isSubmitting ||
                            stageGates?.options?.[plantHex]?.isProjectClosed ||
                            stageGates?.options?.[plantHex]?.[
                              `gate${groupIndex}`
                            ]?.state
                          }
                        >
                          Update
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-light btn-sm float-right mr-2"
                          onClick={() => setSelectedOptionsGroup(undefined)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </Formik>
            </AppModal>
          </Step>
        ))}
      </Steps>
      {stageGates?.groups?.[selectedStageGate]?.title && (
        <h5 className="ml-4 mr-4">
          {stageGates?.groups?.[selectedStageGate]?.title}
        </h5>
      )}
    </>
  );
};

export default GCLStages;

const Steps = styled.ul`
  margin-bottom: 30px;
  overflow: hidden;
  color: #455a64;
  padding-left: 0px;
  margin-top: 30px;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const Step = styled.li`
  list-style-type: none;
  flex-grow: 1;
  position: relative;
  font-weight: 400;
  cursor: pointer;

  .date {
    height: 21px;
  }

  .number {
    width: 90px;
    height: 90px;
    line-height: 45px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: bold;
    background-color: ${(props) => props.gateColor};
    border-radius: 50%;
    margin: 18px auto;
    color: ${(props) =>
      props.gateColor === colors.goWithConditions ? '#000' : '#fff'};
    z-index: 1;
    position: relative;
    border: 4px solid
      ${(props) => (props.isSelected ? '#000' : props.gateColor)};
  }

  /* button {
    @media (max-width: 768px) {
      display: none;
    }
  } */

  /* button {
    position: relative;
    display: inline-block;
    cursor: pointer;
    outline: none;
    border: 0;
    vertical-align: middle;
    text-decoration: none;
    font-size: 1.5rem;
    padding: 15px 16px;
    font-weight: 700;
    text-transform: uppercase;
    font-family: inherit;
  } */

  .progress-line {
    content: '';
    width: 100%;
    height: 4px;
    background-color: ${(props) => props.lineColor};
    position: absolute;
    top: 92px;
    z-index: 0;
    left: -50%;
    @media (min-width: 768px) {
      top: 121px;
    }
  }

  &:first-child .progress-line {
    display: none;
  }
`;

const FiltersList = styled.ul`
  padding-left: 0px;
  list-style: none;
`;

const EvidenceFileList = styled.ul`
  list-style: none;
  margin: 10px 0 0 0;
  padding-left: 6px;
  background-color: ${(props) => (props.urls ? colors.grey200 : 'initial')};
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;
const YellowBtn = styled.button`
  background-color: ${colors.goWithConditions};
`;
const AprovedDateWrapper = styled.div`
  min-width: 180px;
`;
const FileName = styled.a`
  text-decoration: ${(props) => (props.isRemoved ? 'line-through' : 'unset')};
`;

const ChangeStateBtn = styled.button`
  position: relative;
  display: inline-block;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  outline: none;
  font-size: 0.8rem;
  color: #000;
  /* font-weight: normal; */
  padding: 4px 20px;
  border: 2px solid #000;
  border-radius: 3px;
  background-color: ${(props) => (props.color ? props.color : colors.grey500)};
  transform-style: preserve-3d;
  transition: all 175ms cubic-bezier(0, 0, 1, 1);
  &[disabled] {
    background-color: ${(props) =>
      minions.colors.lightenDarkenColor(props.color, 80)};
  }
  &::before {
    position: absolute;
    content: '';
    width: 100%;
    height: 100%;
    top: -6px;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${colors.grey500};
    border-radius: inherit;
    transform: translate3d(0, 0.75em, -1em);
    transition: all 175ms cubic-bezier(0, 0, 1, 1);
  }

  &:active:not([disabled]) {
    transform: translate(0em, 0.25em);
  }

  &:active:not([disabled])::before {
    transform: translate3d(0, 0.6em, -1em);
  }
`;
